import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rapportsApi } from '../api/rapports.api';
import { PeriodeSelector } from '../../bulletins/components/PeriodeSelector';
import { useProjets, useProjet } from '../../administration/hooks/useProjets';
import { useEmployes } from '../../employes/hooks/useEmployes';

const now = new Date();

export function LivrePaiePage() {
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [idProjet, setIdProjet] = useState<string | undefined>(undefined);

  const { data: projets } = useProjets({ actif: true });
  const listeProjets = (projets as any[]) ?? [];
  const estFiltreAucunProjet = idProjet === '__AUCUN__';
  const { data: projetDetail } = useProjet(estFiltreAucunProjet ? undefined : idProjet);
  const { data: employesSansProjet, isLoading: employesSansProjetCharge } = useEmployes(
    { idProjet: '__AUCUN__', actif: true },
    { enabled: estFiltreAucunProjet }
  );

  // null = pas de filtre, 'chargement' = en attente, Set = prêt
  const idsEmployesProjet: null | 'chargement' | Set<string> = estFiltreAucunProjet
    ? employesSansProjetCharge
      ? 'chargement'
      : new Set((employesSansProjet?.items ?? []).map((e: any) => e.idEmploye))
    : idProjet
      ? projetDetail
        ? new Set((projetDetail as any).affectations?.map((a: any) => a.idEmploye) ?? [])
        : 'chargement'
      : null;

  const { data: rawData, isFetching, isError, refetch } = useQuery({
    queryKey: ['livre-paie', { mois, annee }],
    queryFn: () => rapportsApi.livrePaie(annee, mois),
    enabled: false,
  });

  const data = (() => {
    if (!rawData) return rawData;
    if (!idsEmployesProjet || idsEmployesProjet === 'chargement') return rawData;
    const lignesFiltrees = rawData.lignes.filter((l: any) => (idsEmployesProjet as Set<string>).has(l.idEmploye));
    return {
      ...rawData,
      lignes: lignesFiltrees,
      totaux: lignesFiltrees.reduce(
        (acc: any, l: any) => ({
          salaireBase: acc.salaireBase + l.salaireBase,
          salaireBrut: acc.salaireBrut + l.salaireBrut,
          cnssEmploye: acc.cnssEmploye + l.cnssEmploye,
          cnssPatronal: acc.cnssPatronal + l.cnssPatronal,
          its: acc.its + l.its,
          vps: acc.vps + l.vps,
          salaireNet: acc.salaireNet + l.salaireNet,
          netAPayer: acc.netAPayer + l.netAPayer,
        }),
        { salaireBase: 0, salaireBrut: 0, cnssEmploye: 0, cnssPatronal: 0, its: 0, vps: 0, salaireNet: 0, netAPayer: 0 }
      ),
    };
  })();

  const f = (n: number) => n?.toLocaleString('fr-FR') ?? '—';

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-content mb-6">Livre de paie</h1>

      <div className="flex gap-4 mb-6 flex-wrap items-center">
        <PeriodeSelector mois={mois} annee={annee} onChange={(m, a) => { setMois(m); setAnnee(a); }} />
        {listeProjets.length > 0 && (
          <select
            value={idProjet ?? ''}
            onChange={e => setIdProjet(e.target.value || undefined)}
            className="border border-border rounded px-3 py-2 text-sm"
          >
            <option value="">Tous les projets</option>
            {listeProjets.map((p: any) => (
              <option key={p.idProjet} value={p.idProjet}>{p.nom}</option>
            ))}
          </select>
        )}
        <button onClick={() => refetch()} disabled={isFetching} className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {isFetching ? 'Génération...' : 'Générer'}
        </button>
      </div>

      {isError && <p className="text-amber-600 text-sm">Aucun bulletin pour cette période.</p>}

      {data && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-surface-raised">
              <tr>
                {['Employé', 'Base', 'Brut', 'CNSS sal.', 'CNSS pat.', 'ITS', 'VPS', 'Net', 'À payer'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-medium text-content-subtle uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-border">
              {data.lignes.map((l: any) => (
                <tr key={l.idEmploye} className="hover:bg-surface-raised">
                  <td className="px-3 py-2 font-medium">{l.nom} {l.prenoms}</td>
                  <td className="px-3 py-2">{f(l.salaireBase)}</td>
                  <td className="px-3 py-2">{f(l.salaireBrut)}</td>
                  <td className="px-3 py-2 text-red-600">{f(l.cnssEmploye)}</td>
                  <td className="px-3 py-2 text-orange-600">{f(l.cnssPatronal)}</td>
                  <td className="px-3 py-2 text-red-600">{f(l.its)}</td>
                  <td className="px-3 py-2 text-orange-600">{f(l.vps)}</td>
                  <td className="px-3 py-2">{f(l.salaireNet)}</td>
                  <td className="px-3 py-2 font-semibold text-green-700">{f(l.netAPayer)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-surface-overlay font-semibold">
              <tr>
                <td className="px-3 py-2">TOTAUX</td>
                <td className="px-3 py-2">{f(data.totaux.salaireBase)}</td>
                <td className="px-3 py-2">{f(data.totaux.salaireBrut)}</td>
                <td className="px-3 py-2 text-red-600">{f(data.totaux.cnssEmploye)}</td>
                <td className="px-3 py-2 text-orange-600">{f(data.totaux.cnssPatronal)}</td>
                <td className="px-3 py-2 text-red-600">{f(data.totaux.its)}</td>
                <td className="px-3 py-2 text-orange-600">{f(data.totaux.vps)}</td>
                <td className="px-3 py-2">{f(data.totaux.salaireNet)}</td>
                <td className="px-3 py-2 text-green-700">{f(data.totaux.netAPayer)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
